import type { ElementType, ReactNode } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Close from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

export type RightDrawerWidth = "xs" | "sm" | "md" | "lg" | "xl";

/** Fluent-style panel widths for compact Microsoft forms. */
const WIDTH: Record<RightDrawerWidth, number> = {
  xs: 360,
  sm: 420,
  md: 480,
  lg: 560,
  xl: 640,
};

const WIDTH_TOKENS = new Set<string>(Object.keys(WIDTH));

export function resolveRightDrawerWidth(
  maxWidth?: RightDrawerWidth | false | null,
  fallback: RightDrawerWidth = "sm",
): number {
  if (!maxWidth || !WIDTH_TOKENS.has(maxWidth)) return WIDTH[fallback];
  return WIDTH[maxWidth];
}

function resolvePaperWidth(width?: RightDrawerWidth | number | string | false): number | string {
  if (typeof width === "number" && Number.isFinite(width)) return width;
  if (typeof width === "string" && WIDTH_TOKENS.has(width)) return WIDTH[width as RightDrawerWidth];
  if (typeof width === "string" && width.trim()) return width;
  return WIDTH.sm;
}

export type RightDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  width?: RightDrawerWidth | number | string | false;
  children?: ReactNode;
  footer?: ReactNode;
  component?: ElementType;
  componentProps?: Record<string, unknown>;
};

export default function RightDrawer({
  open,
  onClose,
  title,
  subtitle,
  width = "sm",
  children,
  footer,
  component = "div",
  componentProps,
}: RightDrawerProps) {
  const { t } = useTranslation();
  const resolvedWidth = resolvePaperWidth(width);
  const panelWidth = typeof resolvedWidth === "number" ? `${resolvedWidth}px` : resolvedWidth;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: panelWidth },
            maxWidth: "100vw",
            height: "100%",
            maxHeight: "100%",
            top: 0,
            right: "0 !important",
            left: "auto !important",
            margin: 0,
            borderRadius: 0,
            borderLeft: 1,
            borderColor: "divider",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "-8px 0 24px rgba(0,0,0,0.45)"
                : "-8px 0 24px rgba(0,0,0,0.12)",
            whiteSpace: "normal !important",
            overflowX: "hidden",
            zIndex: (theme) => theme.zIndex.modal,
          },
        },
      }}
    >
      <Box
        component={component}
        {...componentProps}
        sx={{
          height: "100%",
          minHeight: 0,
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            minHeight: 52,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: 0,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton type="button" onClick={onClose} aria-label={t("common.close")} size="small" sx={{ mt: -0.25 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 2 }}>{children}</Box>
        {footer ? (
          <>
            <Divider />
            <Box
              sx={{
                px: 2.5,
                py: 1.25,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                bgcolor: "background.paper",
              }}
            >
              {footer}
            </Box>
          </>
        ) : null}
      </Box>
    </Drawer>
  );
}
